import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Landmark, Menu, ShieldCheck, Trash2, User } from "lucide-react";
import API from "../services/api";
import BottomNav from "../components/BottomNav";

function BankAccounts() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ bank_name: "", account_holder: "", account_number: "", ifsc: "" });

  const loadAccounts = useCallback(async () => {
    if (!user?.id) return;
    const res = await API.get(`/bank-accounts/${user.id}`);
    setAccounts(Array.isArray(res.data) ? res.data : []);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) { navigate("/login"); return; }
    loadAccounts();
  }, [loadAccounts, navigate, user?.id]);

  const updateField = (field, value) => setForm((c) => ({ ...c, [field]: value }));

  const saveAccount = async () => {
    if (!form.account_number || !form.account_holder || !form.ifsc) {
      alert("Enter complete bank details");
      return;
    }
    await API.post("/bank-accounts", { ...form, user_id: user.id, is_default: true });
    setForm({ bank_name: "", account_holder: "", account_number: "", ifsc: "" });
    loadAccounts();
  };

  const deleteAccount = async (id) => {
    await API.delete(`/bank-accounts/${id}`);
    loadAccounts();
  };

  return (
    <div style={page}>
      {/* Header */}
      <header style={header}>
        <button type="button" style={menuBtn} onClick={() => navigate(-1)}>
          <Menu size={22} color="#fff" />
        </button>
        <span style={headerTitle}>Bank Details</span>
      </header>

      <main style={content}>
        {/* Form */}
        <div style={formRow}>
          <InputField
            placeholder="Enter account number"
            icon={<Landmark size={18} color="#ea580c" />}
            value={form.account_number}
            onChange={(v) => updateField("account_number", v)}
            inputMode="numeric"
          />
          <InputField
            placeholder="Enter account name"
            icon={<User size={18} color="#ea580c" />}
            value={form.account_holder}
            onChange={(v) => updateField("account_holder", v)}
          />
        </div>

        <div style={formRowSingle}>
          <InputField
            placeholder="Enter bank IFSC code"
            icon={<KeyRound size={18} color="#ea580c" />}
            value={form.ifsc}
            onChange={(v) => updateField("ifsc", v.toUpperCase())}
          />
          <button type="button" style={updateBtn} onClick={saveAccount}>
            <Landmark size={16} color="#fff" />
            Update Bank
          </button>
        </div>

        {/* Saved accounts */}
        {accounts.length > 0 && (
          <div style={savedList}>
            {accounts.map((account) => (
              <div key={account.id} style={savedCard}>
                <div style={savedTop}>
                  <span style={savedBankBadge}><Landmark size={18} /></span>
                  <div style={{ flex: 1 }}>
                    <strong style={savedBank}>{account.bank_name || account.account_holder}</strong>
                    <div style={savedHolder}>{account.account_holder}</div>
                  </div>
                  <button type="button" style={deleteBtn} onClick={() => deleteAccount(account.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
                <div style={savedDetails}>
                  <span style={detailPill}>AC: {maskAccount(account.account_number)}</span>
                  <span style={detailPill}>IFSC: {account.ifsc}</span>
                  <span style={readyBadge}><ShieldCheck size={12} /> Ready</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
}

function InputField({ placeholder, icon, value, onChange, inputMode }) {
  return (
    <div style={inputWrap}>
      <input
        style={inputField}
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        inputMode={inputMode}
      />
      <span style={inputIcon}>{icon}</span>
    </div>
  );
}

function maskAccount(value) {
  const clean = String(value || "");
  if (clean.length <= 4) return clean;
  return `${"*".repeat(Math.max(0, clean.length - 4))}${clean.slice(-4)}`;
}

const page = { minHeight: "100vh", background: "#f1f5f9", paddingBottom: 80 };

const header = {
  background: "linear-gradient(135deg, #ea580c 0%, #7c2d12 100%)",
  height: 58,
  display: "flex",
  alignItems: "center",
  padding: "0 16px",
  gap: 12,
};
const menuBtn = { border: 0, background: "transparent", display: "grid", placeItems: "center", padding: 4, cursor: "pointer" };
const headerTitle = { fontSize: 18, fontWeight: 800, color: "#fff" };

const content = { padding: "24px 16px", display: "grid", gap: 14 };

const formRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const formRowSingle = { display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center" };

const inputWrap = { position: "relative" };
const inputField = {
  width: "100%",
  height: 50,
  border: "1.5px solid #fed7aa",
  borderRadius: 999,
  padding: "0 44px 0 18px",
  fontSize: 14,
  color: "#0f172a",
  background: "#fff",
  outline: "none",
  boxSizing: "border-box",
};
const inputIcon = {
  position: "absolute",
  right: 14,
  top: "50%",
  transform: "translateY(-50%)",
  display: "grid",
  placeItems: "center",
};

const updateBtn = {
  height: 50,
  padding: "0 20px",
  border: 0,
  borderRadius: 999,
  background: "#431407",
  color: "#fff",
  fontWeight: 800,
  fontSize: 14,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 8,
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const savedList = { display: "grid", gap: 12, marginTop: 8 };
const savedCard = { background: "#fff", borderRadius: 16, padding: "14px 16px", display: "grid", gap: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #e5e7eb" };
const savedTop = { display: "flex", alignItems: "center", gap: 12 };
const savedBankBadge = { width: 40, height: 40, borderRadius: 12, background: "#ffedd5", color: "#ea580c", display: "grid", placeItems: "center", flexShrink: 0 };
const savedBank = { fontSize: 14, fontWeight: 800, color: "#0f172a" };
const savedHolder = { fontSize: 12, color: "#64748b", marginTop: 2 };
const deleteBtn = { width: 34, height: 34, border: 0, borderRadius: 10, background: "#fef2f2", color: "#dc2626", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 };
const savedDetails = { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" };
const detailPill = { fontSize: 11, fontWeight: 700, color: "#ea580c", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 999, padding: "3px 10px" };
const readyBadge = { fontSize: 11, fontWeight: 700, color: "#059669", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 999, padding: "3px 10px", display: "flex", alignItems: "center", gap: 4 };

export default BankAccounts;
